import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MainLayout } from '@/layouts/MainLayout';
import { Spinner } from '@/components/ui/Spinner';
import { store } from '@/store';
import '@/i18n';
import '@/styles/globals.css';

// Lazy load pages for code splitting
const Home = lazy(() => import('@/pages/Home').then(module => ({ default: module.Home })));
const About = lazy(() => import('@/pages/About').then(module => ({ default: module.About })));
const Dashboard = lazy(() => import('@/pages/Dashboard').then(module => ({ default: module.Dashboard })));
const Patients = lazy(() => import('@/pages/Patients').then(module => ({ default: module.Patients })));
const PatientRecord = lazy(() => import('@/pages/PatientRecord').then(module => ({ default: module.PatientRecord })));
const Calendar = lazy(() => import('@/pages/Calendar').then(module => ({ default: module.Calendar })));
const Reports = lazy(() => import('@/pages/Reports').then(module => ({ default: module.Reports })));
const Messages = lazy(() => import('@/pages/Messages').then(module => ({ default: module.Messages })));
const Administration = lazy(() => import('@/pages/Administration').then(module => ({ default: module.Administration })));

// Loading fallback component
const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <Spinner size="lg" />
  </div>
);

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<MainLayout />}>
              <Route index element={
                <Suspense fallback={<PageLoader />}>
                  <Dashboard />
                </Suspense>
              } />
              <Route path="about" element={
                <Suspense fallback={<PageLoader />}>
                  <About />
                </Suspense>
              } />
              <Route path="patients" element={
                <Suspense fallback={<PageLoader />}>
                  <Patients />
                </Suspense>
              } />
              <Route path="patients/:id" element={
                <Suspense fallback={<PageLoader />}>
                  <PatientRecord />
                </Suspense>
              } />
              <Route path="calendar" element={
                <Suspense fallback={<PageLoader />}>
                  <Calendar />
                </Suspense>
              } />
              <Route path="reports" element={
                <Suspense fallback={<PageLoader />}>
                  <Reports />
                </Suspense>
              } />
              <Route path="messages" element={
                <Suspense fallback={<PageLoader />}>
                  <Messages />
                </Suspense>
              } />
              <Route path="administration/*" element={
                <Suspense fallback={<PageLoader />}>
                  <Administration />
                </Suspense>
              } />
            </Route>
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </Provider>
  );
};

export default App;

